import { ReactNode, useEffect, useMemo, useState } from 'react'

import { IconAlert, IconCheck, IconExport } from '@avantstay/backoffice-vectors'

import { Button } from '../Button'
import { FrameButton, FrameButtonType } from '../Frame'
import { Loading } from '../Loading'
import {
  ConfirmationFormValue,
  ConfirmationModal,
  ConfirmationModalProps,
} from './components/ConfirmationModal'
import * as S from './ExportCsv.styles'

enum CsvExportStatusEnum {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

interface ExportCsvProps
  extends
    FrameButtonType,
    Pick<
      ConfirmationModalProps,
      | 'numberOfRecordsToExport'
      | 'recordsLimit'
      | 'recordsLimitMessage'
      | 'sensitiveInformationCheckbox'
    > {
  userEmail?: string
  buttonText?: string
  isLoading?: boolean
  isDisabled?: boolean
  onExport: (
    value?: Pick<ConfirmationFormValue, 'includeSensitiveInformation'>,
  ) => Promise<unknown> | unknown

  // NOTE (Iori): These properties were added to keep compatibility with ExportDataToCsv
  // We can remove them when this component is no longer used.
  isGeneratedOnClient?: boolean
  customButtonTitle?: ReactNode
}

export const ExportCsv = ({
  userEmail,
  buttonText = 'Export CSV',
  shouldFrameButton,
  isLoading,
  isDisabled,
  onExport,
  isGeneratedOnClient,
  customButtonTitle,
  ...confirmationModalProps
}: ExportCsvProps) => {
  const [csvExportStatus, setCsvExportStatus] = useState(CsvExportStatusEnum.IDLE)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const currentButtonText = useMemo(() => {
    if (csvExportStatus === CsvExportStatusEnum.LOADING) {
      return <S.LoadingContainer>Preparing report. Keep browser window open</S.LoadingContainer>
    }

    if (csvExportStatus === CsvExportStatusEnum.SUCCESS) {
      return (
        <S.MessageContainer>
          {isGeneratedOnClient ? 'Report generated' : ` Report will be sent to ${userEmail}`}{' '}
        </S.MessageContainer>
      )
    }

    if (csvExportStatus === CsvExportStatusEnum.ERROR) {
      return <S.ErrorContainer>Could not send the report. Try again later</S.ErrorContainer>
    }

    return <>{buttonText}</>
  }, [csvExportStatus, userEmail, buttonText, isGeneratedOnClient])

  const currentButtonIcon = useMemo(() => {
    if (csvExportStatus === CsvExportStatusEnum.LOADING) {
      return (
        <S.LoadingContainer>
          <Loading size="4px" />
        </S.LoadingContainer>
      )
    }

    if (csvExportStatus === CsvExportStatusEnum.SUCCESS) {
      return (
        <S.MessageContainer>
          <IconCheck />
        </S.MessageContainer>
      )
    }

    if (csvExportStatus === CsvExportStatusEnum.ERROR) {
      return (
        <S.ErrorContainer>
          <IconAlert />
        </S.ErrorContainer>
      )
    }

    return <IconExport />
  }, [csvExportStatus])

  const handleExport = async (
    value?: Pick<ConfirmationFormValue, 'includeSensitiveInformation'>,
  ) => {
    setCsvExportStatus(CsvExportStatusEnum.LOADING)

    try {
      const result = await onExport(value)

      setCsvExportStatus(CsvExportStatusEnum.SUCCESS)
      return result
    } catch {
      setCsvExportStatus(CsvExportStatusEnum.ERROR)
    } finally {
      setTimeout(() => setCsvExportStatus(CsvExportStatusEnum.IDLE), 5_000)
    }
  }

  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    const isSuccessOrError = [CsvExportStatusEnum.SUCCESS, CsvExportStatusEnum.ERROR].includes(
      csvExportStatus,
    )

    if (isSuccessOrError) {
      timeoutId = setTimeout(() => setCsvExportStatus(CsvExportStatusEnum.IDLE), 5_000)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [csvExportStatus])

  const isButtonDisabled = isLoading || isDisabled || csvExportStatus !== CsvExportStatusEnum.IDLE

  return (
    <>
      {customButtonTitle ? (
        <Button.Primary disabled={isButtonDisabled} onClick={() => setIsModalOpen(true)}>
          {customButtonTitle}
        </Button.Primary>
      ) : (
        <FrameButton
          buttonText={currentButtonText}
          icon={currentButtonIcon}
          disabled={isButtonDisabled}
          shouldFrameButton={shouldFrameButton}
          onClick={() => setIsModalOpen(true)}
        />
      )}

      <ConfirmationModal
        {...confirmationModalProps}
        isOpen={isModalOpen}
        onConfirm={value => {
          setIsModalOpen(false)
          handleExport(value)
        }}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  )
}
